/**
 * Map State Management
 * Sanitizes, loads, and saves map state with validation
 */
import { sessionStorage as safeSessionStorage } from '../storage'
import { isValidLatitude, isValidLongitude, isValidLocation, isValidRadius } from './coordinates'

/**
 * Sanitizes and validates persisted map state from storage
 * @param {Object} state - State object from storage
 * @returns {Object|null} Sanitized state or null if invalid
 */
export function sanitizeMapState(state) {
  if (!state || typeof state !== 'object') return null
  
  const sanitized = {}
  
  // Validate and sanitize userLocation
  if (state.userLocation && isValidLocation(state.userLocation)) {
    sanitized.userLocation = {
      latitude: Number(state.userLocation.latitude),
      longitude: Number(state.userLocation.longitude)
    }
  }
  
  // Validate and sanitize radius
  if (state.radius !== undefined && isValidRadius(state.radius)) {
    sanitized.radius = Number(state.radius)
  }
  
  // Sanitize string fields (prevent XSS)
  if (typeof state.searchLocation === 'string') {
    sanitized.searchLocation = state.searchLocation.slice(0, 500) // Max length
  }
  
  if (typeof state.searchLocationName === 'string') {
    sanitized.searchLocationName = state.searchLocationName.slice(0, 500)
  }
  
  // Validate and sanitize entities array
  if (Array.isArray(state.entities)) {
    sanitized.entities = state.entities
      .filter(entity => {
        // Basic validation: must have required fields
        return entity && 
               typeof entity === 'object' && 
               typeof entity.id === 'string' &&
               typeof entity.name === 'string' &&
               isValidLocation(entity)
      })
      .map(entity => ({
        // Only include safe, expected fields
        id: String(entity.id).slice(0, 100),
        entityId: entity.entityId ? String(entity.entityId).slice(0, 100) : entity.id,
        name: String(entity.name).slice(0, 255),
        description: entity.description ? String(entity.description).slice(0, 1000) : undefined,
        currentLatitude: Number(entity.currentLatitude),
        currentLongitude: Number(entity.currentLongitude),
        currentAddress: entity.currentAddress ? String(entity.currentAddress).slice(0, 500) : undefined,
        confirmationCount: typeof entity.confirmationCount === 'number' ? entity.confirmationCount : 0,
        lastSeenAt: entity.lastSeenAt ? String(entity.lastSeenAt).slice(0, 100) : undefined,
        imageUrl: entity.imageUrl ? String(entity.imageUrl).slice(0, 500) : undefined,
        category: entity.category ? String(entity.category).slice(0, 100) : undefined,
        type: entity.type ? String(entity.type).slice(0, 100) : undefined,
        isSighting: Boolean(entity.isSighting),
        isLatest: entity.isLatest !== undefined ? Boolean(entity.isLatest) : undefined,
        markerOpacity: typeof entity.markerOpacity === 'number' ? entity.markerOpacity : undefined,
        sightingIndex: typeof entity.sightingIndex === 'number' ? entity.sightingIndex : undefined,
        trailColor: entity.trailColor ? String(entity.trailColor).slice(0, 50) : undefined,
        distance: typeof entity.distance === 'number' ? entity.distance : undefined
      }))
  }
  
  // Validate and sanitize entityMovementTrails array
  if (Array.isArray(state.entityMovementTrails)) {
    sanitized.entityMovementTrails = state.entityMovementTrails
      .filter(trail => {
        return trail &&
               typeof trail === 'object' &&
               (typeof trail.entityId === 'number' || typeof trail.entityId === 'string') &&
               typeof trail.entityName === 'string' &&
               Array.isArray(trail.coordinates) &&
               trail.coordinates.length >= 2 // Need at least 2 points for a trail
      })
      .map(trail => ({
        entityId: typeof trail.entityId === 'number' ? trail.entityId : parseInt(trail.entityId),
        entityName: String(trail.entityName).slice(0, 255),
        color: trail.color ? String(trail.color).slice(0, 50) : undefined,
        coordinates: trail.coordinates
          .filter(coord => Array.isArray(coord) && coord.length === 2)
          .map(coord => [Number(coord[0]), Number(coord[1])])
          .filter(coord => isValidLatitude(coord[0]) && isValidLongitude(coord[1]))
      }))
      .filter(trail => trail.coordinates.length >= 2) // Re-filter after coordinate validation
  }
  
  return sanitized
}

/**
 * Safely loads map state from sessionStorage with validation
 * @param {string} key - Storage key
 * @returns {Object|null} Sanitized state or null
 */
export function loadMapState(key) {
  try {
    const saved = safeSessionStorage.get(key)
    if (!saved) return null
    
    return sanitizeMapState(saved)
  } catch (error) {
    // Clear corrupted data and return null
    safeSessionStorage.remove(key)
    return null
  }
}

/**
 * Safely saves map state to sessionStorage
 * @param {string} key - Storage key
 * @param {Object} state - State to save
 */
export function saveMapState(key, state) {
  try {
    const sanitized = sanitizeMapState(state)
    if (sanitized) {
      safeSessionStorage.set(key, sanitized)
    }
  } catch (error) {
    // Silent failure - not critical for map functionality
  }
}
